import type { ActivityLog } from '@/types'

type ActivityRouteCtx = {
  /**
   * Current client id (from `ProfileTab` context). Used for cases like
   * document events where we always route back to the client's documents
   * section.
   */
  clientId?: string | null
}

export type ActivityTargetType =
  | 'quote'
  | 'contract'
  | 'invoice'
  | 'payment'
  | 'job'
  | 'report'
  | 'job_report'
  | 'credit_note'

export type ActivityTarget = {
  type: ActivityTargetType
  id: string
}

const ROUTE_BUILDERS: Record<string, (id: string) => string> = {
  quote: id => `/quotes/${id}`,
  contract: id => `/contracts/${id}`,
  invoice: id => `/invoices/${id}`,
  payment: id => `/payments/${id}`,
  job: id => `/jobs/${id}`,
  report: id => `/calendar?job=${id}&tab=reports`,
  job_report: id => `/calendar?job=${id}&tab=reports`,
  credit_note: () => '/credit-notes',
}

function normalizeTargetType(value: string): ActivityTargetType | null {
  const type = value.toLowerCase()
  if (type === 'intervention') return 'job'
  return type in ROUTE_BUILDERS ? (type as ActivityTargetType) : null
}

export function activityTarget(
  a: Pick<ActivityLog, 'subject_type' | 'subject_id' | 'properties'>
): ActivityTarget | null {
  const props = a.properties ?? {}
  const linkType = typeof props.link_type === 'string' ? props.link_type : null
  const linkId = typeof props.link_id === 'string' ? props.link_id : null
  if (linkType && linkId) {
    const type = normalizeTargetType(linkType)
    if (type) return { type, id: linkId }
  }

  // Document activities attributed to a client retain the actual owner in
  // their metadata. Job/intervention documents must open the job drawer rather
  // than fall through to the client's Documents tab.
  const documentOwnerType =
    typeof props.document_owner_type === 'string'
      ? props.document_owner_type.toLowerCase()
      : null
  const documentOwnerId =
    typeof props.document_owner_id === 'string' ? props.document_owner_id : null
  if (
    documentOwnerId &&
    (documentOwnerType === 'job' || documentOwnerType === 'intervention')
  ) {
    return { type: 'job', id: documentOwnerId }
  }

  const reportJobId = typeof props.job_id === 'string' ? props.job_id : null
  if (reportJobId) return { type: 'report', id: reportJobId }

  if (a.subject_type && a.subject_id) {
    const short = (
      a.subject_type.split('\\').pop() ?? a.subject_type
    ).toLowerCase()
    const type = normalizeTargetType(short)
    if (type) return { type, id: a.subject_id }
  }

  return null
}

// Deep-link an activity row to the related entity's detail page. Prefers an
// explicit properties.link_type / link_id (set when the row is attributed to
// a client), then falls back to the morph subject (subject_type / subject_id).
//
// Client-subject events (`client.*`) are intentionally non-linkable: they are
// "update within this client" and should not redirect away.
export function activityRoute(
  a: Pick<ActivityLog, 'subject_type' | 'subject_id' | 'properties'> & {
    event_type?: string | null
  },
  ctx: ActivityRouteCtx = {}
): string | null {
  // Recent Activity Preview should not redirect for in-client updates.
  const eventType = a.event_type ?? null
  if (eventType?.startsWith('client.')) return null

  const target = activityTarget(a)
  if (target) {
    const builder = ROUTE_BUILDERS[target.type]
    if (builder) return builder(target.id)
  }

  // Documents are always owned by some entity, but for the Recent Activity
  // preview we want them to redirect back into the current client's "Documents"
  // tab. This avoids deep-linking to non-existing document detail pages.
  if (eventType?.startsWith('document.') && ctx.clientId) {
    return `/clients/${ctx.clientId}?tab=documents`
  }

  return null
}
